import { Geld } from '../lib/geld';
import { feestToeslag } from '../lib/feest';
import { useMemo, useRef, useState } from 'react';
import {
  addDays,
  eachDayOfInterval,
  endOfWeek,
  format,
  isSameDay,
  startOfWeek,
} from 'date-fns';
import { nl } from 'date-fns/locale';
import { cn, formatMoney, formatTime } from '../lib/utils';
import { VerlengdTag } from './Verlengd';
import type { Holiday, ShiftWithCalc } from '../types';
import { shiftKind, KIND_LABEL, KIND_STYLE } from '../lib/shift-kind';

interface Props {
  cursor: Date;
  shifts: ShiftWithCalc[];
  holidayMap: Map<string, Holiday>;
  onShiftClick: (shiftId: number) => void;
  onEmptySlotClick: (date: string, startTime: string) => void;
  onShiftMove: (shiftId: number, newDate: string) => void;
  onShiftResize: (shiftId: number, newEndTime: string, newDate?: string) => void;
}

const HOUR_HEIGHT = 40; // px per uur
const HOURS = Array.from({ length: 24 }, (_, i) => i);

type Drag =
  | { kind: 'move'; shiftId: number }
  | { kind: 'resize'; shiftId: number; origEnd: string; origDate: string };

export function CalendarWeekView({
  cursor,
  shifts,
  holidayMap,
  onShiftClick,
  onEmptySlotClick,
  onShiftMove,
  onShiftResize,
}: Props) {
  const weekStart = startOfWeek(cursor, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(cursor, { weekStartsOn: 1 });
  const days = useMemo(() => eachDayOfInterval({ start: weekStart, end: weekEnd }), [weekStart, weekEnd]);
  const today = format(new Date(), 'yyyy-MM-dd');
  const [drag, setDrag] = useState<Drag | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const shiftsByDate = useMemo(() => {
    const map = new Map<string, ShiftWithCalc[]>();
    for (const s of shifts) {
      if (!map.has(s.shift_date)) map.set(s.shift_date, []);
      map.get(s.shift_date)!.push(s);
    }
    return map;
  }, [shifts]);

  function handleDragStart(e: React.DragEvent, d: Drag) {
    setDrag(d);
    e.dataTransfer.effectAllowed = 'move';
    // Needed for Firefox
    e.dataTransfer.setData('text/plain', JSON.stringify(d));
  }

  function handleDayDrop(e: React.DragEvent, dateStr: string) {
    e.preventDefault();
    if (!drag) return;
    if (drag.kind === 'move') {
      const shift = shifts.find((s) => s.id === drag.shiftId);
      if (shift && shift.shift_date !== dateStr) {
        onShiftMove(drag.shiftId, dateStr);
      }
    } else if (drag.kind === 'resize') {
      // Resize over cel-grens: compute eindtijd on basis van Y
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const yInDay = e.clientY - rect.top;
      const hour = Math.max(0, Math.min(24, Math.round((yInDay / HOUR_HEIGHT) * 4) / 4));
      const hh = String(Math.floor(hour)).padStart(2, '0');
      const mm = String(Math.round((hour - Math.floor(hour)) * 60)).padStart(2, '0');
      const newEnd = `${hh === '24' ? '00' : hh}:${mm}`;
      onShiftResize(drag.shiftId, newEnd, dateStr);
    }
    setDrag(null);
  }

  function handleEmptySlotClick(e: React.MouseEvent, dateStr: string) {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const y = e.clientY - rect.top;
    const hour = Math.max(0, Math.min(23, Math.floor(y / HOUR_HEIGHT)));
    const hh = String(hour).padStart(2, '0');
    onEmptySlotClick(dateStr, `${hh}:00`);
  }

  return (
    <div className="card-flush enter">
      {/* Header met dag-namen */}
      <div className="grid border-b border-surface-line/70" style={{ gridTemplateColumns: '44px repeat(7, minmax(0, 1fr))' }}>
        <div />
        {days.map((d) => {
          const ds = format(d, 'yyyy-MM-dd');
          const isToday = ds === today;
          const holiday = holidayMap.get(ds);
          return (
            <div
              key={ds}
              className="min-w-0 border-l border-surface-line/50 px-1 py-2 text-center"
            >
              <div className="t-label">{format(d, 'EEE', { locale: nl })}</div>
              <div
                className={cn(
                  't-num mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold',
                  isToday ? 'bg-brand-400 text-slate-950' : 'text-slate-100'
                )}
              >
                {format(d, 'd')}
              </div>
              {holiday && (
                <div className="truncate text-xs text-rose-300" title={`${holiday.name}: ${feestToeslag(ds).lang}`}>
                  <span className="hidden sm:inline">{holiday.name} {feestToeslag(ds).kort}</span>
                  <span className="mx-auto mt-0.5 block h-1.5 w-1.5 rounded-full bg-rose-400 sm:hidden" aria-hidden />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div
        ref={gridRef}
        className="grid relative overflow-y-auto max-h-[calc(100vh-240px)]"
        style={{ gridTemplateColumns: '44px repeat(7, minmax(0, 1fr))' }}
      >
        {/* Uur-kolom links */}
        <div>
          {HOURS.map((h) => (
            <div
              key={h}
              className="t-num pr-1.5 text-right text-xs text-slate-500 -translate-y-2 first:translate-y-0"
              style={{ height: HOUR_HEIGHT }}
            >
              {h === 0 ? '' : String(h).padStart(2, '0')}
            </div>
          ))}
        </div>

        {/* 7 dagkolommen */}
        {days.map((d) => {
          const ds = format(d, 'yyyy-MM-dd');
          const dayShifts = shiftsByDate.get(ds) ?? [];
          const isToday = ds === today;
          const isHoliday = holidayMap.has(ds);
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;

          return (
            <div
              key={ds}
              onClick={(e) => handleEmptySlotClick(e, ds)}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDrop={(e) => handleDayDrop(e, ds)}
              className={cn(
                'relative border-l border-surface-line/50 cursor-pointer',
                isWeekend && 'bg-slate-950/25',
                isHoliday && 'bg-rose-500/5',
                isToday && 'bg-brand-400/[0.04]'
              )}
              style={{ height: HOUR_HEIGHT * 24 }}
            >
              {/* Uurlijnen */}
              {HOURS.map((h) => (
                <div
                  key={h}
                  className="absolute left-0 right-0 border-t border-surface-line/40"
                  style={{ top: h * HOUR_HEIGHT }}
                />
              ))}

              {/* Shift blokken (starten op deze dag) */}
              {dayShifts.map((s) => (
                <ShiftBlock
                  key={s.id}
                  shift={s}
                  onClick={(e) => {
                    e.stopPropagation();
                    onShiftClick(s.id);
                  }}
                  onDragStart={handleDragStart}
                />
              ))}

              {/* Doorloop-blokken: nachtdiensten die gisteren begonnen en na middernacht
                  op deze dag doorlopen (00:00 tot eindtijd). Anders lijkt deze dag leeg
                  terwijl je tot 's ochtends werkt. */}
              {(shiftsByDate.get(format(addDays(d, -1), 'yyyy-MM-dd')) ?? [])
                .filter((s) => s.crosses_midnight)
                .map((s) => (
                  <ShiftContinuation
                    key={`cont-${s.id}`}
                    shift={s}
                    onClick={(e) => {
                      e.stopPropagation();
                      onShiftClick(s.id);
                    }}
                  />
                ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ShiftBlock({
  shift,
  onClick,
  onDragStart,
}: {
  shift: ShiftWithCalc;
  onClick: (e: React.MouseEvent) => void;
  onDragStart: (e: React.DragEvent, d: Drag) => void;
}) {
  const [sh, sm] = shift.start_time.split(':').map(Number);
  const [eh, em] = shift.end_time.split(':').map(Number);
  const startPos = (sh + sm / 60) * HOUR_HEIGHT;
  const endHour = shift.crosses_midnight ? eh + 24 : eh;
  // Clamp op 24:00 — het deel na middernacht wordt op de volgende dag getekend (ShiftContinuation).
  const endPos = Math.min(24 * HOUR_HEIGHT, (endHour + em / 60) * HOUR_HEIGHT);
  const height = Math.max(20, endPos - startPos);
  const kind = shiftKind(shift);
  const st = KIND_STYLE[kind];

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, { kind: 'move', shiftId: shift.id })}
      onClick={onClick}
      onKeyDown={keyActivate}
      role="button"
      tabIndex={0}
      className={cn(
        'absolute left-0.5 right-0.5 cursor-move overflow-hidden rounded-md border-l-2 px-1 py-1 transition-[filter] duration-fast ease-calm hover:brightness-125 sm:left-1 sm:right-1 sm:px-1.5',
        st.bar,
        st.bg
      )}
      style={{ top: startPos, height }}
      title={`${KIND_LABEL[kind]} ${formatTime(shift.start_time)}–${formatTime(shift.end_time)}, ${shift.object_naam_snapshot ?? ''}`}
    >
      <div className={cn('t-num text-xs font-semibold leading-tight', st.text)}>
        {formatTime(shift.start_time)}
        <span className="hidden sm:inline">–{formatTime(shift.end_time)}</span>
        <VerlengdTag shift={shift} className="ml-1 hidden sm:inline" />
      </div>
      {height > 45 && (
        <div className="hidden sm:block truncate text-xs text-slate-300">
          {shift.object?.naam_kort ?? shift.object_naam_snapshot ?? KIND_LABEL[kind]}
        </div>
      )}
      {height > 70 && shift.total_gross > 0 && (
        <div className="t-num hidden truncate text-xs text-slate-400 sm:block"><Geld bruto={shift.total_gross} date={shift.shift_date} /></div>
      )}

      {/* Resize handle aan onderkant */}
      <div
        draggable
        onDragStart={(e) => {
          e.stopPropagation();
          onDragStart(e, {
            kind: 'resize',
            shiftId: shift.id,
            origEnd: shift.end_time,
            origDate: shift.shift_date,
          });
        }}
        onClick={(e) => e.stopPropagation()}
        className="absolute bottom-0 left-0 right-0 h-1.5 cursor-ns-resize hover:bg-white/20"
        aria-label="Eindtijd verslepen"
        title="Sleep om de eindtijd te wijzigen"
      />
    </div>
  );
}

/** Het na-middernacht-deel van een nachtdienst, getekend op de volgende dag (00:00 tot eindtijd). */
function ShiftContinuation({
  shift,
  onClick,
}: {
  shift: ShiftWithCalc;
  onClick: (e: React.MouseEvent) => void;
}) {
  const [eh, em] = shift.end_time.split(':').map(Number);
  const endPos = (eh + em / 60) * HOUR_HEIGHT;
  const height = Math.max(20, endPos); // vanaf 00:00
  const kind = shiftKind(shift);
  const st = KIND_STYLE[kind];

  return (
    <div
      onClick={onClick}
      onKeyDown={keyActivate}
      role="button"
      tabIndex={0}
      className={cn(
        'absolute left-0.5 right-0.5 cursor-pointer overflow-hidden rounded-md border-l-2 border-dashed px-1 py-1 opacity-80 transition-[filter] duration-fast ease-calm hover:brightness-125 sm:left-1 sm:right-1 sm:px-1.5',
        st.bar,
        st.bg
      )}
      style={{ top: 0, height }}
      title={`Vervolg nachtdienst van gisteren, tot ${formatTime(shift.end_time)}`}
    >
      <div className={cn('t-num text-xs font-semibold leading-tight', st.text)}>
        <span className="hidden sm:inline">tot </span>{formatTime(shift.end_time)}
      </div>
      {height > 45 && (
        <div className="hidden sm:block truncate text-xs text-slate-400">
          {shift.object?.naam_kort ?? shift.object_naam_snapshot ?? '–'}
        </div>
      )}
    </div>
  );
}

/** Enter/spatie op een blok werkt als een klik. */
function keyActivate(e: React.KeyboardEvent<HTMLElement>) {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    e.currentTarget.click();
  }
}
